import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { analyticsApi, inventoryApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  DollarSign, 
  ClipboardList, 
  AlertTriangle, 
  Users,
  UserCheck,
  Plus,
  ArrowRight,
  Wrench,
  Clock,
  CheckCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const statusColors = {
  draft: 'status-draft',
  pending: 'status-pending',
  approved: 'status-approved',
  ordered: 'status-ordered',
  delivered: 'status-delivered',
};

export default function Dashboard() {
  const { user, isInstaller } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, lowStockRes] = await Promise.all([
          analyticsApi.getSummary(),
          inventoryApi.getLowStock()
        ]);
        setAnalytics(analyticsRes.data);
        setLowStock(lowStockRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Data fetch runs once on mount
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  const stats = isInstaller ? [
    {
      title: 'Scheduled Jobs',
      value: analytics?.scheduled_installations || 0,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'In Progress',
      value: analytics?.in_progress_installations || 0,
      icon: Wrench,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      title: 'Completed',
      value: analytics?.completed_installations || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
  ] : [
    {
      title: 'Total Spending',
      value: `$${(analytics?.total_spending || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Open POs',
      value: analytics?.open_pos || 0,
      icon: ClipboardList,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Customers',
      value: analytics?.total_customers || 0,
      icon: UserCheck,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      link: '/customers',
    },
    {
      title: 'Low Stock Items',
      value: analytics?.low_stock_count || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Welcome back, {user?.name}
          </p>
        </div>
        {!isInstaller && (
          <div className="flex gap-2">
            <Link to="/purchase-orders/new">
              <Button className="rounded-sm" data-testid="btn-new-po">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">New PO</span>
                <span className="sm:hidden">PO</span>
              </Button>
            </Link>
            <Link to="/installations/new">
              <Button variant="outline" className="rounded-sm" data-testid="btn-new-installation">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">New Job</span>
                <span className="sm:hidden">Job</span>
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className={`grid grid-cols-2 ${isInstaller ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4 lg:gap-6`} data-testid="stats-grid">
        {stats.map((stat) => (
          <Card key={stat.title} className="border border-border shadow-none rounded-sm" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground truncate">{stat.title}</p>
                  <p className="text-lg lg:text-2xl font-semibold font-mono-data mt-1 truncate">{stat.value}</p>
                </div>
                <div className={`p-2 lg:p-3 rounded-sm ${stat.bgColor} shrink-0`}>
                  <stat.icon className={`w-4 h-4 lg:w-5 lg:h-5 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!isInstaller && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Vendors Chart */}
          <Card className="lg:col-span-2 border border-border shadow-none rounded-sm" data-testid="top-vendors-chart">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Top Vendors by Spending</CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6">
              {analytics?.top_vendors?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.top_vendors} layout="vertical">
                    <XAxis type="number" tickFormatter={(v) => `$${v.toLocaleString()}`} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Total']}
                      contentStyle={{ borderRadius: '4px', border: '1px solid hsl(30, 15%, 85%)' }}
                    />
                    <Bar dataKey="total" fill="hsl(25, 95%, 53%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                  No vendor data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent POs */}
          <Card className="border border-border shadow-none rounded-sm" data-testid="recent-pos">
            <CardHeader className="border-b border-border/50 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading">Recent Orders</CardTitle>
                <Link to="/purchase-orders" className="text-xs text-primary hover:underline flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {analytics?.recent_pos?.length > 0 ? (
                <div className="divide-y divide-border">
                  {analytics.recent_pos.map((po) => (
                    <Link 
                      key={po.id} 
                      to={`/purchase-orders/${po.id}`}
                      className="block p-4 hover:bg-muted/50 transition-colors"
                      data-testid={`recent-po-${po.po_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="font-mono-data text-sm font-medium">{po.po_number}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{po.vendor_name}</p>
                        </div>
                        <Badge className={`${statusColors[po.status]} rounded-none text-xs uppercase tracking-wider shrink-0 ml-2`}>
                          {po.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  No purchase orders yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Low Stock Alert */}
      {!isInstaller && lowStock.length > 0 && (
        <Card className="border-2 border-red-200 bg-red-50 shadow-none rounded-sm" data-testid="low-stock-alert">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {lowStock.slice(0, 6).map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between bg-white p-3 rounded-sm border border-red-200"
                  data-testid={`low-stock-${item.product_id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{item.product_name || 'Unknown Product'}</p>
                    <p className="text-xs text-muted-foreground font-mono-data">{item.product_sku}</p>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="font-mono-data text-sm font-semibold text-red-600">{item.quantity}</p>
                    <p className="text-xs text-muted-foreground">of {item.reorder_level} min</p>
                  </div>
                </div>
              ))}
            </div>
            {lowStock.length > 6 && (
              <Link to="/inventory" className="block mt-4 text-sm text-primary hover:underline">
                View all {lowStock.length} low stock items →
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
